import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ClearOutlined,
  EyeOutlined,
  FilterOutlined,
  InboxOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { caseAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import MobileFilterSheet from '../components/mobile/MobileFilterSheet';
import MobileSectionCard from '../components/mobile/MobileSectionCard';
import { CALC_TYPE_LABELS, getDrugLabel } from '../constants/drugs';
import type { Case } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;

const statusConfig: Record<string, { color: string; label: string }> = {
  active: { color: 'green', label: '进行中' },
  closed: { color: 'default', label: '已关闭' },
  archived: { color: 'orange', label: '已归档' },
};

const formatCaseTime = (value?: string) => (value ? new Date(value).toLocaleString('zh-CN') : '-');

const CaseList: React.FC = () => {
  const navigate = useNavigate();
  const { isMobileShell } = useResponsive();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterDrug, setFilterDrug] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const loadCases = async () => {
    try {
      setLoading(true);
      const data = await caseAPI.list(undefined, filterStatus || 'active', {
        page, page_size: pageSize, search: searchText || undefined, drug_name: filterDrug,
      });
      if (Array.isArray(data)) { setCases(data); setTotal(data.length); }
      else { setCases(data.items); setTotal(data.total); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCases(); }, [page, pageSize, searchText, filterDrug, filterStatus]);

  const drugOptions = useMemo(() => {
    const drugs = Array.from(new Set(cases.map((item) => item.drug_name).filter(Boolean)));
    return drugs.map((item) => ({ label: getDrugLabel(item), value: item }));
  }, [cases]);

  const filteredCases = cases;

  const hasActiveFilters = Boolean(filterDrug || filterStatus);

  const clearAllFilters = () => {
    setSearchText('');
    setFilterDrug(undefined);
    setFilterStatus(undefined);
    setPage(1);
  };

  const handleArchive = async (caseId: string) => {
    try {
      await caseAPI.archive(caseId);
      message.success('病例已归档');
      loadCases();
    } catch {
      message.error('归档失败');
    }
  };

  const columns = [
    {
      title: '患者编号',
      dataIndex: 'patient_code',
      key: 'patient_code',
      width: 160,
      render: (value: string | undefined, record: Case) => value || record.patient_id,
    },
    {
      title: '药物',
      dataIndex: 'drug_name',
      key: 'drug_name',
      width: 120,
      render: (value: string) => <Tag color="blue">{getDrugLabel(value)}</Tag>,
    },
    {
      title: '诊断摘要',
      dataIndex: 'diagnosis',
      key: 'diagnosis',
      render: (value: string | undefined) => value || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: string) => {
        const config = statusConfig[value] || { color: 'default', label: value };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '最近更新',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: formatCaseTime,
    },
    {
      title: '操作',
      key: 'actions',
      width: 170,
      render: (_: unknown, record: Case) => (
        <Space size={4}>
          <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/cases/${record.id}`)}>
            查看
          </Button>
          <Popconfirm
            title="确认归档此病例？"
            onConfirm={() => handleArchive(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" danger icon={<InboxOutlined />}>
              归档
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filterFields = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Select
        placeholder="按药物筛选"
        allowClear
        value={filterDrug}
        onChange={setFilterDrug}
        options={drugOptions}
      />
      <Select
        placeholder="按状态筛选"
        allowClear
        value={filterStatus}
        onChange={setFilterStatus}
        options={[
          { label: '进行中', value: 'active' },
          { label: '已关闭', value: 'closed' },
        ]}
      />
    </Space>
  );

  const renderMobileCards = () => {
    if (!filteredCases.length) {
      return <Empty description="暂无病例数据，请先新建病例" style={{ padding: '48px 0' }} />;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredCases.map((item) => {
          const config = statusConfig[item.status] || { color: 'default', label: item.status };
          return (
            <MobileSectionCard
              key={item.id}
              title={item.patient_code || item.patient_id}
              extra={<Tag color={config.color}>{config.label}</Tag>}
            >
              <Space wrap size={[8, 8]} style={{ marginBottom: 10 }}>
                <Tag color="blue">{getDrugLabel(item.drug_name)}</Tag>
                {item.last_calculation_type ? (
                  <Tag color="purple">{CALC_TYPE_LABELS[item.last_calculation_type] || item.last_calculation_type}</Tag>
                ) : null}
              </Space>

              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>诊断摘要</Text>
                <Text>{item.diagnosis || '暂无诊断信息'}</Text>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>更新时间</Text>
                <Text>{formatCaseTime(item.updated_at)}</Text>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  type="primary"
                  icon={<EyeOutlined />}
                  block
                  onClick={() => navigate(`/cases/${item.id}`)}
                >
                  查看详情
                </Button>
                <Popconfirm
                  title="确认归档此病例？"
                  onConfirm={() => handleArchive(item.id)}
                  okText="确认"
                  cancelText="取消"
                >
                  <Button icon={<InboxOutlined />} block>
                    归档
                  </Button>
                </Popconfirm>
              </div>
            </MobileSectionCard>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobileShell ? 'flex-start' : 'center',
          flexDirection: isMobileShell ? 'column' : 'row',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>病例列表</Title>
          <Text type="secondary">查看当前活跃病例，并在移动端以卡片方式快速操作。</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadCases} loading={loading}>
          刷新
        </Button>
      </div>

      {isMobileShell ? (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <Search
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onSearch={setSearchText}
              placeholder="搜索患者编号或诊断"
              allowClear
              style={{ flex: 1 }}
            />
            <Button icon={<FilterOutlined />} onClick={() => setFilterOpen(true)}>
              筛选
            </Button>
          </div>

          {hasActiveFilters ? (
            <div style={{ marginBottom: 12 }}>
              <Button type="link" icon={<ClearOutlined />} onClick={clearAllFilters} style={{ paddingInline: 0 }}>
                清空筛选
              </Button>
            </div>
          ) : null}

          {renderMobileCards()}

          <MobileFilterSheet
            open={filterOpen}
            title="筛选病例"
            onClose={() => setFilterOpen(false)}
            onReset={clearAllFilters}
            onApply={() => setFilterOpen(false)}
          >
            {filterFields}
          </MobileFilterSheet>
        </>
      ) : (
        <>
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[12, 12]} align="middle">
              <Col flex="1 1 240px">
                <Search
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  onSearch={setSearchText}
                  placeholder="搜索患者编号或诊断"
                  allowClear
                />
              </Col>
              <Col flex="0 1 160px">
                <Select
                  style={{ width: '100%' }}
                  placeholder="按药物筛选"
                  allowClear
                  value={filterDrug}
                  onChange={setFilterDrug}
                  options={drugOptions}
                />
              </Col>
              <Col flex="0 1 140px">
                <Select
                  style={{ width: '100%' }}
                  placeholder="按状态筛选"
                  allowClear
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { label: '进行中', value: 'active' },
                    { label: '已关闭', value: 'closed' },
                  ]}
                />
              </Col>
              {hasActiveFilters ? (
                <Col>
                  <Button type="link" icon={<ClearOutlined />} onClick={clearAllFilters}>
                    清空筛选
                  </Button>
                </Col>
              ) : null}
            </Row>
          </Card>

          <Card>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredCases}
              loading={loading}
              scroll={{ x: 920 }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                onChange: (nextPage, nextSize) => {
                  if (nextSize !== pageSize) { setPageSize(nextSize); setPage(1); }
                  else setPage(nextPage);
                },
                showTotal: (total) => `共 ${total} 条病例`,
              }}
              locale={{ emptyText: '暂无病例数据，请先新建病例' }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default CaseList;
